import React from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Stack,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import { LocalShipping as DeliveryIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { format } from 'date-fns';
import type { BulkChannelsEditorProps } from './types';

export const BulkChannelsEditor: React.FC<BulkChannelsEditorProps> = ({
  channelsData,
  onChannelsDataChange,
  availableChannels,
  isLoadingChannels,
}) => {
  const updateAggregator = (channel: string, updates: Partial<{ enabled: boolean; include?: boolean; deactivationReason?: string; deactivationExpiry?: Date }>) => {
    onChannelsDataChange({
      ...channelsData,
      aggregators: {
        ...channelsData.aggregators,
        [channel]: {
          ...channelsData.aggregators[channel],
          ...updates,
        },
      },
    });
  };

  const isOnlineOrdersIncluded = channelsData.includeOnlineOrders ?? false;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Check the items you want to modify. Unchecked items will not be changed.
        <br />
        <Typography variant="caption" component="span" sx={{ mt: 0.5, display: 'block' }}>
          Delivery aggregator changes may take a few minutes to reflect. Online Orders changes are immediate.
        </Typography>
      </Alert>

      <Paper
        sx={{
          p: 2,
          mb: 2,
          opacity: isOnlineOrdersIncluded ? 1 : 0.5,
          bgcolor: isOnlineOrdersIncluded ? 'background.paper' : 'action.hover',
          transition: 'opacity 0.2s, background-color 0.2s',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            checked={isOnlineOrdersIncluded}
            onChange={(e) => onChannelsDataChange({
              ...channelsData,
              includeOnlineOrders: e.target.checked,
            })}
            sx={{ p: 0.5 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={channelsData.acceptingOnlineOrders}
                onChange={(e) => onChannelsDataChange({
                  ...channelsData,
                  acceptingOnlineOrders: e.target.checked
                })}
                disabled={!isOnlineOrdersIncluded}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Accept Online Orders (Website & Mobile App)</span>
                {!isOnlineOrdersIncluded && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    (won't be modified)
                  </Typography>
                )}
              </Box>
            }
            labelPlacement="start"
            sx={{ flex: 1, justifyContent: 'space-between', ml: 0, mr: 0 }}
          />
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, mb: 2 }}>
        <DeliveryIcon sx={{ color: 'text.secondary' }} />
        <Typography variant="subtitle2">
          Delivery Aggregators
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {isLoadingChannels ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : availableChannels.length === 0 ? (
        <Alert severity="info">
          No delivery channels are configured for this organization.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {availableChannels.map((channel) => {
            const isEnabled = channelsData.aggregators[channel.channel]?.enabled ?? true;
            const channelData = channelsData.aggregators[channel.channel];
            const isIncluded = channelData?.include ?? false;

            return (
              <Paper
                key={channel.channel}
                sx={{
                  p: 2,
                  opacity: isIncluded ? 1 : 0.5,
                  bgcolor: isIncluded ? 'background.paper' : 'action.hover',
                  transition: 'opacity 0.2s, background-color 0.2s',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      checked={isIncluded}
                      onChange={(e) => updateAggregator(channel.channel, { include: e.target.checked })}
                      sx={{ p: 0.5 }}
                    />
                    {!isEnabled && isIncluded && (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          border: '2px solid',
                          borderColor: 'error.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            width: 14,
                            height: 2,
                            bgcolor: 'error.main',
                            transform: 'rotate(-45deg)',
                          }}
                        />
                      </Box>
                    )}
                    <Typography variant="body1" fontWeight={500}>
                      {channel.displayName}
                    </Typography>
                    {!isIncluded && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        (won't be modified)
                      </Typography>
                    )}
                    {!isEnabled && isIncluded && (
                      <Chip
                        label="Inactive"
                        size="small"
                        sx={{
                          bgcolor: 'error.50',
                          color: 'error.main',
                          border: '1px solid',
                          borderColor: 'error.200',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />
                    )}
                  </Box>
                  <Switch
                    checked={isEnabled}
                    onChange={(e) => updateAggregator(channel.channel, {
                      enabled: e.target.checked,
                      // Clear expiry when re-enabling
                      ...(e.target.checked && { deactivationExpiry: undefined })
                    })}
                    disabled={!isIncluded}
                  />
                </Box>

                {!isEnabled && (
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Reason (optional)"
                          size="small"
                          fullWidth
                          value={channelData?.deactivationReason || ''}
                          onChange={(e) => updateAggregator(channel.channel, {
                            deactivationReason: e.target.value
                          })}
                          placeholder="e.g., Kitchen maintenance"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DateTimePicker
                            label="Reactivates at"
                            value={channelData?.deactivationExpiry || null}
                            onChange={(newValue) => updateAggregator(channel.channel, {
                              deactivationExpiry: newValue || undefined
                            })}
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </Grid>

                    {/* Quick Options */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Quick Options
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {/* Hourly options */}
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {[1, 2, 4, 6, 12].map((hours) => (
                            <Chip
                              key={hours}
                              label={`${hours} H`}
                              onClick={() => {
                                const expiry = new Date();
                                expiry.setHours(expiry.getHours() + hours);
                                updateAggregator(channel.channel, { deactivationExpiry: expiry });
                              }}
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Stack>

                        {/* Daily options */}
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          <Chip
                            label="End of Day (4 AM)"
                            onClick={() => {
                              const tomorrow = new Date();
                              tomorrow.setDate(tomorrow.getDate() + 1);
                              tomorrow.setHours(4, 0, 0, 0);
                              updateAggregator(channel.channel, { deactivationExpiry: tomorrow });
                            }}
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            label="Tonight (11:59 PM)"
                            onClick={() => {
                              const tonight = new Date();
                              tonight.setHours(23, 59, 59, 999);
                              updateAggregator(channel.channel, { deactivationExpiry: tonight });
                            }}
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            label="Tomorrow"
                            onClick={() => {
                              const tomorrow = new Date();
                              tomorrow.setDate(tomorrow.getDate() + 1);
                              tomorrow.setHours(new Date().getHours(), new Date().getMinutes(), 0, 0);
                              updateAggregator(channel.channel, { deactivationExpiry: tomorrow });
                            }}
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            label="1 Week"
                            onClick={() => {
                              const nextWeek = new Date();
                              nextWeek.setDate(nextWeek.getDate() + 7);
                              updateAggregator(channel.channel, { deactivationExpiry: nextWeek });
                            }}
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            label="1 Month"
                            onClick={() => {
                              const nextMonth = new Date();
                              nextMonth.setMonth(nextMonth.getMonth() + 1);
                              updateAggregator(channel.channel, { deactivationExpiry: nextMonth });
                            }}
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            label="Indefinitely"
                            onClick={() => {
                              updateAggregator(channel.channel, { deactivationExpiry: undefined });
                            }}
                            variant={!channelData?.deactivationExpiry ? 'filled' : 'outlined'}
                            color={!channelData?.deactivationExpiry ? 'primary' : 'default'}
                            size="small"
                          />
                        </Stack>
                      </Stack>
                    </Box>

                    {channelData?.deactivationExpiry && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Channel will reactivate: {format(channelData.deactivationExpiry, 'MMM d, h:mm a')}
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};
